import { getTabbables } from 'cadenza/utils/event-util';
import { uniqueId } from 'cadenza/utils/unique-id';

import './legacy-fieldset.css';

const COMPONENT_NAME = 'd-legacy-fieldset';

/**
 * A fieldset grouping controls and their labels. You may provide a `<label>` with the
 * `.d-legacy-fieldset--legend` CSS class as a direct child of the group.
 *
 * Disabling the fieldset disables the controls inside of it. Add the `.is-fieldset-disabled-hidden`
 * CSS class to elements, which should be hidden instead.
 *
 * Note: Only elements _after_ the fieldset label are disabled or hidden. You can add controls,
 * which control the fieldset disabled property, before that label.
 */
export class LegacyFieldset extends HTMLElement {

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    this.setAttribute('role', 'group');

    setTimeout(() => { // Light DOM ready
      const legend = this.legend;
      if (legend) {
        if (!legend.id) {
          legend.id = uniqueId();
        }
        this.setAttribute('aria-labelledby', legend.id);
      }

      this._applyDisabledStateToDescendants();
    });
  }

  /**
   * @protected
   * @return {Element} The legend element
   */
  get legend () {
    return this.querySelector(`.${COMPONENT_NAME}--legend`);
  }

  set disabled (value) {
    if (value === this.disabled) {
      return;
    }

    if (value) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.removeAttribute('aria-disabled');
    }

    this._applyDisabledStateToDescendants();
  }

  _applyDisabledStateToDescendants () {
    if (this.disabled) {
      const children = [ ...this.children ];
      const legendIndex = children.indexOf(this.legend);
      // disable controls inside this fieldset (and after the legend)
      getTabbables(this).forEach(el => {
        if (typeof el.disabled === 'boolean') {
          const closestChild = el.closest(`.${COMPONENT_NAME} > *`);
          const isAfterLegend = children.indexOf(closestChild) > legendIndex;
          if (isAfterLegend) {
            el.dataset.previousDisabled = String(el.disabled);
            el.disabled = true;
          }
        }
      });
    } else {
      // restore the previous disabled state
      this.querySelectorAll('[data-previous-disabled]').forEach(el => {
        el.disabled = (el.dataset.previousDisabled === 'true');
        delete el.dataset.previousDisabled;
      });
    }
  }

  get disabled () {
    return this.getAttribute('aria-disabled') === 'true';
  }

}

customElements.define(COMPONENT_NAME, LegacyFieldset);
